/**
 * SpacerPropertiesEditor Component
 * 
 * Property editor for Spacer block type.
 */

import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { SpacerBlockProps } from '@/types/menuBuilder';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface SpacerPropertiesEditorProps {
  props: SpacerBlockProps;
  onUpdate: (props: Partial<SpacerBlockProps>) => void;
}

export const SpacerPropertiesEditor = memo(function SpacerPropertiesEditor({
  props,
  onUpdate,
}: SpacerPropertiesEditorProps) {
  const { t } = useTranslation();

  // Preset heights for quick selection
  const heightPresets = [
    { value: 8, label: 'XS (8px)' },
    { value: 16, label: 'SM (16px)' },
    { value: 24, label: 'MD (24px)' },
    { value: 32, label: 'LG (32px)' },
    { value: 48, label: 'XL (48px)' },
    { value: 64, label: '2XL (64px)' },
  ];

  return (
    <div className="space-y-6">
      {/* Quick Presets */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.spacerPreset', 'Quick Preset')}</Label>
        <Select
          value={String(props.height)}
          onValueChange={(value) => onUpdate({ height: Number(value) })}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select size" />
          </SelectTrigger>
          <SelectContent>
            {heightPresets.map((preset) => (
              <SelectItem key={preset.value} value={String(preset.value)}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Custom Height Slider */}
      <div className="space-y-2">
        <Label>
          {t('menuBuilder.customHeight', 'Custom Height')}: {props.height}px
        </Label>
        <Slider
          value={[props.height || 24]}
          onValueChange={([value]) => onUpdate({ height: value })}
          min={4}
          max={120}
          step={4}
        />
      </div>

      {/* Preview */}
      <div className="space-y-2">
        <Label>{t('menuBuilder.preview', 'Preview')}</Label>
        <div className="border rounded-lg p-2 bg-muted/30">
          <div 
            className="bg-primary/20 rounded"
            style={{ height: props.height || 24 }}
          />
        </div>
      </div>
    </div>
  );
});

export default SpacerPropertiesEditor;
